'use client';

import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import Input from '@/components/platform/forms/Input';
import { PrimaryButton, SecondaryButton } from '@/components/platform/buttons';
import { signUp, authClient } from '@/lib/platform/auth/client';

interface DemoSignupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DemoSignupModal({ isOpen, onClose }: DemoSignupModalProps) {
  const { darkMode } = useDarkMode();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Save demo request and send sales notification email
      const demoResponse = await fetchWithBypass('/api/platform/demo/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          company,
        }),
      });

      if (!demoResponse.ok) {
        const errorData = await demoResponse.json();
        throw new Error(errorData.error || 'Failed to submit demo request');
      }

      // Step 2: Create anonymous user account with Better Auth
      // Use random demo email so users can access demo multiple times
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const anonymousEmail = `demo-${timestamp}-${randomId}@valiyou.demo`;
      const randomPassword =
        Math.random().toString(36).slice(-12) + Math.random().toString(36).slice(-12);

      const signupResult = await signUp.email({
        email: anonymousEmail, // Use anonymous email for Better Auth
        password: randomPassword,
        name: name, // Use real name from form
        callbackURL: '/products/valuations/dashboard',
      });

      if (signupResult.error) {
        throw new Error(signupResult.error.message || 'Failed to create account');
      }

      // Step 3: Add user to demo organization
      const joinResponse = await fetchWithBypass('/api/platform/demo/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recaptchaToken: 'demo-signup-bypass', // Special bypass token for demo signup flow
        }),
      });

      if (!joinResponse.ok) {
        const joinError = await joinResponse.json();
        throw new Error(joinError.error || 'Failed to join demo organization');
      }

      // Step 4: Refresh session to get updated isAnonymous flag from database
      // This ensures the client-side session includes the isAnonymous flag
      // before navigating to the dashboard (prevents showing real name initially)
      await authClient.getSession();

      // Step 5: Set flag to prevent redirect loop during demo signup
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('demoSignupInProgress', 'true');
      }

      // Step 6: Redirect to demo dashboard with full page reload
      // Use window.location.href to clear all React Query cache and state
      // This ensures fresh organization data, avatar, and logo are loaded
      // CRITICAL FIX: Include ?org= parameter to help organization context load faster
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = '/products/valuations/dashboard?org=demo-org-2025';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to access demo');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setName('');
    setEmail('');
    setCompany('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleReset} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          className={`relative mx-auto w-full max-w-lg overflow-hidden rounded-lg border border-blue-500 p-12 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}
        >
          {/* Title with close button */}
          <div className="mb-2 flex items-center justify-between">
            <Dialog.Title
              className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
            >
              Instant Demo Access!
            </Dialog.Title>

            <button
              type="button"
              onClick={handleReset}
              className={`rounded-md p-1 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <p
            className={`mb-4 text-base font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Try the full platform now - ready in seconds
          </p>

          <ul
            className={`mb-6 flex flex-col space-y-2 text-sm md:flex-row md:space-y-0 md:space-x-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            <li className="flex items-start">
              <svg
                className="mt-0.5 mr-2 size-5 shrink-0 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              No credit card!
            </li>
            <li className="flex items-start">
              <svg
                className="mt-0.5 mr-2 size-5 shrink-0 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Instant access!
            </li>
            <li className="flex items-start">
              <svg
                className="mt-0.5 mr-2 size-5 shrink-0 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              No installation!
            </li>
          </ul>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Full name"
              type="text"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="John Doe"
            />

            <Input
              label="Email address"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="john@company.com"
            />

            <Input
              label="Company / Organization"
              type="text"
              name="organization"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              placeholder="Your organization"
            />

            {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

            {/* Privacy note */}
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              By accessing the demo, you agree to our privacy policy. We use your information to
              provide you with the best demo experience.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <SecondaryButton onClick={handleReset} type="button" disabled={loading}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" loading={loading}>
                Get Demo Access →
              </PrimaryButton>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
